// Menu Assistência (2026-10-16) — backend src/assistance-center/. Guinchos
// cadastrados pela empresa, atendimento das solicitações registradas pelo
// app ("Registrar Assistência") e painel com gráficos.
import { gpsDriveDelete, gpsDriveGet, gpsDrivePost, gpsDrivePut } from "@/lib/api/gpsdrive-client";

export type AssistanceCenterAccess = { view: boolean; manage: boolean; requests: boolean; cancel: boolean };

export const TRUCK_TYPES = [
  { key: "plataforma", label: "Plataforma" },
  { key: "asa_delta", label: "Asa delta" },
  { key: "reboque", label: "Reboque/cambão" },
  { key: "pesado", label: "Pesado" },
  { key: "moto", label: "Moto" },
  { key: "outro", label: "Outro" },
] as const;
export type TruckType = (typeof TRUCK_TYPES)[number]["key"];

export const REQUEST_STATUSES = [
  { key: "aberto", label: "Aberto" },
  { key: "em_andamento", label: "Em andamento" },
  { key: "concluido", label: "Concluído" },
  { key: "cancelado", label: "Cancelado" },
] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number]["key"];

export interface TowTruckInput {
  name: string;
  contactName: string | null;
  phone: string | null;
  whatsapp: string | null;
  document: string | null;
  truckType: TruckType;
  plate: string | null;
  address: string | null;
  city: string;
  state: string;
  latitude: number | null;
  longitude: number | null;
  coverageRadiusKm: number;
  baseFee: number | null;
  pricePerKm: number | null;
  available24h: boolean;
  active: boolean;
  traccarDeviceId: number | null;
  notes: string | null;
}

export interface TowTruck extends TowTruckInput {
  id: string;
  createdAt: string;
  updatedAt: string;
  requestsCount: number;
  lastRequestAt: string | null;
}

export interface AssistanceRequestRow {
  id: string;
  serviceTypeId: string;
  serviceTypeName: string;
  clientName: string;
  requestedByName: string;
  traccarDeviceId: number;
  vehicleName: string;
  reason: string | null;
  requestedAt: string;
  status: RequestStatus;
  towTruckId: string | null;
  towTruckName: string | null;
  cost: number | null;
  distanceKm: number | null;
  address: string | null;
  city: string | null;
  state: string | null;
  latitude: number | null;
  longitude: number | null;
  completedAt: string | null;
  notes: string | null;
  // Plano do veículo na aba Clientes (ex.: OURO), se ele tiver assistência.
  assistancePlan?: string | null;
  canceledAt: string | null;
  canceledByName: string | null;
  cancelReason: string | null;
}

export interface UpdateRequestInput {
  status: RequestStatus;
  towTruckId: string | null;
  cost: number | null;
  distanceKm: number | null;
  address: string | null;
  city: string | null;
  state: string | null;
  notes: string | null;
}

interface Bucket {
  name: string;
  count: number;
  cost: number;
}

export interface AssistanceSummary {
  from: string;
  to: string;
  kpis: {
    requests: number;
    towRequests: number;
    open: number;
    completed: number;
    canceled: number;
    totalCost: number;
    towCost: number;
    avgTowCost: number | null;
    avgTowKm: number | null;
    avgResolutionMin: number | null;
    towTrucks: number;
    activeTowTrucks: number;
    cities: number;
    uniqueVehicles: number;
  };
  byMonth: { month: string; total: number; guincho: number; custo: number }[];
  byService: Bucket[];
  byStatus: Bucket[];
  byCity: Bucket[];
  byTowTruck: Bucket[];
  byWeekday: { day: number; count: number }[];
  byHour: { hour: number; count: number }[];
  trucksByCity: { city: string; state: string; total: number; active: number }[];
  recent: AssistanceRequestRow[];
}

export type AssistanceSource = "planilha" | "plano" | "contrato";
export const ASSISTANCE_SOURCE_LABEL: Record<AssistanceSource, string> = {
  planilha: "planilha importada",
  plano: "plano do veículo (Faturamento)",
  contrato: "plano no contrato",
};

export interface AssistanceClientVehicle {
  id: string;
  removable: boolean;
  source: AssistanceSource;
  groupName: string | null;
  plate: string | null;
  vehicleLabel: string | null;
  planName: string;
  traccarDeviceId: number | null;
  deviceName: string | null;
}

export interface AssistanceClient {
  userId: string;
  traccarUserId: number | null;
  name: string;
  email: string;
  cpfCnpj: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  requestsCount: number;
  vehicles: AssistanceClientVehicle[];
}

export interface ImportClientRow {
  name: string;
  cpfCnpj: string | null;
  plate: string | null;
  vehicle: string | null;
  plan: string;
}

export interface ImportClientsResult {
  dryRun: boolean;
  rows: number;
  clientsFound: number;
  clientsNotFound: number;
  vehicles: number;
  vehiclesWithDevice: number;
  vehiclesWithoutDevice: number;
  notFound: { name: string; cpfCnpj: string | null; vehicles: number }[];
  plans: { name: string; count: number }[];
}

export type TowPhase = "aguardando" | "a_caminho" | "no_local" | "levando" | "concluido" | "cancelado";
export const TOW_PHASE_LABEL: Record<TowPhase, string> = {
  aguardando: "Aguardando guincheiro",
  a_caminho: "A caminho do veículo",
  no_local: "No local",
  levando: "Levando ao destino",
  concluido: "Concluído",
  cancelado: "Cancelado",
};

export interface AssistanceDispatch {
  id: string;
  towTruckId: string | null;
  towTruckName: string;
  phone: string | null;
  hasChat: boolean;
  sendStatus: "enviado" | "falhou" | "nao_enviado";
  sendError: string | null;
  sentAt: string | null;
  openedAt: string | null;
  phase: TowPhase;
  startedAt: string | null;
  arrivedAt: string | null;
  departedAt: string | null;
  finishedAt: string | null;
  lastPosition: { lat: number; lng: number; speedKmh: number | null; heading: number | null; at: string | null } | null;
  lastIncomingAt: string | null;
  unread: boolean;
  driverPath: string;
  createdAt: string;
}

export interface DispatchList {
  request: {
    id: string;
    status: RequestStatus;
    serviceTypeName: string;
    clientName: string;
    vehicleName: string;
    traccarDeviceId: number;
    reason: string | null;
    address: string | null;
    latitude: number | null;
    longitude: number | null;
    requestedAt: string;
    trackToken: string | null;
    vehicleShareToken: string | null;
    clientPhone: string | null;
    destinationAddress: string | null;
    destinationLat: number | null;
    destinationLng: number | null;
  };
  dispatches: AssistanceDispatch[];
}

export interface ChatMessage {
  id: string;
  fromMe: boolean;
  text: string;
  at: string;
}

export interface AssistanceTemplates {
  towTemplate: string;
  clientTemplate: string;
  defaults: { towTemplate: string; clientTemplate: string };
  variables: readonly { key: string; label: string }[];
}

// Telas públicas (/guincho/:token e /acompanhar/:token).
export interface PublicTowView {
  phase: TowPhase;
  takenByOther: string | null;
  towTruckName: string;
  service: string;
  clientName: string;
  clientPhone: string | null;
  reason: string | null;
  vehicle: { label: string; name: string | null; model: string | null; color: string | null; plate: string | null };
  pointA: { lat: number; lng: number; fixTime: string | null; speedKmh: number | null; course: number | null; address: string | null } | null;
  pointB: { lat: number | null; lng: number | null; address: string | null } | null;
  startedAt: string | null;
  arrivedAt: string | null;
  departedAt: string | null;
  finishedAt: string | null;
  trackToken: string | null;
  serverTime: string;
}

export interface PublicTrackView {
  phase: TowPhase;
  service: string;
  clientFirstName: string;
  vehicle: { label: string; model: string | null; color: string | null; plate: string | null };
  vehiclePosition: { lat: number; lng: number; fixTime: string } | null;
  destination: { lat: number; lng: number; address: string | null } | null;
  towTruck: { name: string; type: string | null; plate: string | null; phone: string | null } | null;
  towPosition: { lat: number; lng: number; heading: number | null; speedKmh: number | null; at: string | null } | null;
  eta: { distanceM: number; durationS: number; target: "veiculo" | "destino"; geometry: [number, number][] } | null;
  startedAt: string | null;
  arrivedAt: string | null;
  departedAt: string | null;
  finishedAt: string | null;
  serverTime: string;
}

export interface TowRoute {
  distanceM: number;
  durationS: number;
  geometry: [number, number][];
  steps: { instruction: string; distanceM: number; durationS: number; geometryIndex: number }[];
}

const PUBLIC = "/api/v1/public/assistance";
export const gpsDriveAssistancePublicService = {
  tow: (token: string) => gpsDriveGet<PublicTowView>(`${PUBLIC}/tow/${token}`),
  towAction: (token: string, action: "start" | "arrived" | "depart" | "finish") => gpsDrivePost<PublicTowView>(`${PUBLIC}/tow/${token}/${action}`),
  towPosition: (token: string, p: { lat: number; lng: number; speedKmh: number | null; heading: number | null }) =>
    gpsDrivePost<{ ok: boolean }>(`${PUBLIC}/tow/${token}/position`, p),
  towRoute: (token: string, from: { lat: number; lng: number }, target: "a" | "b") =>
    gpsDriveGet<TowRoute>(`${PUBLIC}/tow/${token}/route?fromLat=${from.lat}&fromLng=${from.lng}&target=${target}`),
  track: (token: string) => gpsDriveGet<PublicTrackView>(`${PUBLIC}/track/${token}`),
};

const BASE = "/api/v1/assistance-center";
// Links da mensagem usam o domínio em uso (mesmo critério do /ao-vivo:
// fora de http(s), ex. capacitor://, cai no domínio principal).
const appOrigin = () => (/^https?:/.test(window.location.origin) ? window.location.origin : "https://gpsdrive.com.br");

export const gpsDriveAssistanceCenterService = {
  access: () => gpsDriveGet<AssistanceCenterAccess>(`${BASE}/access`),
  summary: (from: string, to: string) => gpsDriveGet<AssistanceSummary>(`${BASE}/summary?from=${from}&to=${to}`),
  listTowTrucks: () => gpsDriveGet<TowTruck[]>(`${BASE}/tow-trucks`),
  createTowTruck: (input: TowTruckInput) => gpsDrivePost<TowTruck>(`${BASE}/tow-trucks`, input),
  updateTowTruck: (id: string, input: TowTruckInput) => gpsDrivePut<TowTruck>(`${BASE}/tow-trucks/${id}`, input),
  deleteTowTruck: (id: string) => gpsDriveDelete<void>(`${BASE}/tow-trucks/${id}`),
  geocodeText: (q: string) =>
    gpsDriveGet<{ location: { lat: number; lng: number } | null }>(`${BASE}/geocode?${new URLSearchParams({ q }).toString()}`),
  settings: () => gpsDriveGet<AssistanceTemplates>(`${BASE}/settings`),
  saveSettings: (input: { towTemplate: string; clientTemplate: string }) => gpsDrivePut<AssistanceTemplates>(`${BASE}/settings`, input),
  dispatchPreview: (requestId: string, towTruckId?: string) =>
    gpsDriveGet<{ text: string; trackUrl: string | null; pointAUrl: string | null }>(
      `${BASE}/requests/${requestId}/dispatch-preview?${new URLSearchParams({ origin: appOrigin(), ...(towTruckId ? { towTruckId } : {}) }).toString()}`,
    ),
  dispatch: (requestId: string, input: { towTruckIds: string[]; send: boolean; messageOverride: string | null }) =>
    gpsDrivePost<AssistanceDispatch[]>(`${BASE}/requests/${requestId}/dispatch`, { ...input, origin: appOrigin() }),
  listDispatches: (requestId: string) => gpsDriveGet<DispatchList>(`${BASE}/requests/${requestId}/dispatches`),
  notifyClient: (requestId: string, phone: string | null) =>
    gpsDrivePost<{ sent: boolean; text: string }>(`${BASE}/requests/${requestId}/notify-client`, { origin: appOrigin(), phone }),
  chat: (dispatchId: string) => gpsDriveGet<{ messages: ChatMessage[]; phone: string | null }>(`${BASE}/dispatches/${dispatchId}/messages`),
  sendChat: (dispatchId: string, text: string) => gpsDrivePost<void>(`${BASE}/dispatches/${dispatchId}/messages`, { text }),
  cancelDispatch: (dispatchId: string) => gpsDrivePost<void>(`${BASE}/dispatches/${dispatchId}/cancel`),
  geocode: (address: string, city: string, state: string) =>
    gpsDriveGet<{ location: { lat: number; lng: number } | null }>(
      `${BASE}/geocode?${new URLSearchParams({ address, city, state }).toString()}`,
    ),
  listRequests: (params: { from: string; to: string; status?: RequestStatus; towTruckId?: string }) => {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => !!v) as [string, string][]);
    return gpsDriveGet<AssistanceRequestRow[]>(`${BASE}/requests?${q.toString()}`);
  },
  vehiclePlans: () =>
    gpsDriveGet<Record<string, { planName: string; groupName: string | null; source: AssistanceSource }>>(`${BASE}/vehicle-plans`),
  listClients: () => gpsDriveGet<{ clients: AssistanceClient[]; totalVehicles: number }>(`${BASE}/clients`),
  salesRows: () => gpsDriveGet<ImportClientRow[]>(`${BASE}/clients/sales-rows`),
  importClients: (rows: ImportClientRow[], dryRun: boolean) =>
    gpsDrivePost<ImportClientsResult>(`${BASE}/clients/import?dryRun=${dryRun ? 1 : 0}`, { rows }),
  removeClientVehicle: (id: string) => gpsDriveDelete<void>(`${BASE}/clients/vehicles/${id}`),
  cancelRequest: (id: string, reason: string | null) => gpsDrivePost<AssistanceRequestRow>(`${BASE}/requests/${id}/cancel`, { reason }),
  updateRequest: (id: string, input: UpdateRequestInput) =>
    gpsDrivePut<AssistanceRequestRow>(`${BASE}/requests/${id}`, input),
};
